import { supabase } from './supabase';
import {
  sendWelcomeEmail,
  sendDeleteAccountEmail,
  sendEmail,
  sendPasswordChangeSuccessfulEmail,
} from './email';
import type { User, Session } from '@supabase/supabase-js';

const baseURL = `${window.location.protocol}//${window.location.host}`;

const storeURL = `${baseURL}/store`;

export interface AuthError {
  message: string;
  status?: number;
}

export interface SignUpData {
  email: string;
  password: string;
  fullName: string;
  country: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export function getConfirmEmailRedirectTo() {
  return `${window.location.origin}/confirm-email`;
}

export function isEmailConfirmed(user: User | null | undefined): boolean {
  return Boolean(user?.email_confirmed_at);
}

export function needsEmailConfirmation(
  user: User | null | undefined,
  session: Session | null | undefined
): boolean {
  if (!user) return false;
  // Confirm email ON → usually no session until confirmed
  if (!session) return true;
  return !isEmailConfirmed(user);
}

function welcomeSentStorageKey(userId: string) {
  return `solra_welcome_sent_${userId}`;
}

/** Send welcome once after the email is confirmed (or immediately if confirm is disabled). */
export async function sendWelcomeIfNeeded(user: User | null | undefined): Promise<void> {
  if (!user?.email || !isEmailConfirmed(user)) return;

  const key = welcomeSentStorageKey(user.id);
  try {
    if (localStorage.getItem(key)) return;
  } catch {
    // ignore storage failures
  }

  const fullName =
    (user.user_metadata?.full_name as string | undefined)?.trim() ||
    (user.user_metadata?.name as string | undefined)?.trim() ||
    'there';

  try {
    await sendWelcomeEmail(user.email, fullName, storeURL, user.id);
    try {
      localStorage.setItem(key, '1');
    } catch {
      // ignore
    }
  } catch (emailError) {
    console.error('Failed to send welcome email:', emailError);
    // Do not set localStorage — allow later login/confirm to retry.
    // If EMAIL_SYSTEM=v2 logged a failed row with idempotency key, email-retry-cron can recover.
  }
}

export async function resendSignupConfirmation(email: string) {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.toLowerCase().trim(),
    options: {
      emailRedirectTo: getConfirmEmailRedirectTo(),
    },
  });

  if (error) {
    console.error('Resend signup confirmation error:', error);
    throw {
      message: error.message || 'Failed to resend confirmation email. Please try again.',
      status: error.status,
    } as AuthError;
  }

  return { success: true as const };
}

/** If the user has a session but email is not confirmed, sign them out and return blocked. */
export async function enforceEmailConfirmation(
  user: User | null | undefined
): Promise<{ blocked: boolean; email: string | null }> {
  if (!user) return { blocked: false, email: null };
  if (isEmailConfirmed(user)) return { blocked: false, email: user.email ?? null };

  const email = user.email ?? null;
  try {
    await supabase.auth.signOut();
  } catch (err) {
    console.error('Failed to sign out unconfirmed user:', err);
  }
  return { blocked: true, email };
}

// Check if email is already registered
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('check_email_exists', {
      email_to_check: email
    });

    if (error) {
      console.error('Error checking email:', error);
      return false;
    }
    return data || false;
  } catch (err) {
    console.error('Error checking email:', err);
    return false;
  }
}

// Enhanced email check using the Edge Function
export async function checkUserEmailExists(email: string): Promise<boolean> {
  try {
    // Use the database function to check if email exists
    const { data, error } = await supabase.rpc('check_email_exists', {
      email_to_check: email.toLowerCase().trim()
    });

    if (error) {
      console.error('Error checking user email:', error);
      return false;
    }
    return data || false;
  } catch (err) {
    console.error('Error checking user email:', err);
    return false;
  }
}

// Get user by email for purchase flow
export async function getUserByEmail(email: string): Promise<{ userId: string | null; exists: boolean } | null> {
  try {
    // Use the database function to get user details by email
    const { data, error } = await supabase.rpc('get_user_by_email', {
      email_to_check: email.toLowerCase().trim()
    });

    if (error) {
      console.error('Error finding user by email:', error);
      return null;
    }

    // The function returns an array, get the first result
    const userData = Array.isArray(data) && data.length > 0 ? data[0] : null;

    if (userData && userData.user_exists && userData.user_id) {
      return {
        exists: true,
        userId: userData.user_id
      };
    } else {
      return {
        exists: false,
        userId: null
      };
    }
  } catch (err) {
    console.error('Error finding user by email:', err);
    return null;
  }
}

// Create anonymous user for purchases - simplified with just email and role
export async function createAnonymousUser(email: string): Promise<{ userId: string; success: boolean }> {
  try {
    // First check if email already exists
    const emailExists = await checkEmailExists(email);
    if (emailExists) {
      throw new Error('An account with this email already exists. Please use a different email or sign in to your existing account.');
    }

    // Generate a simple random password
    const randomPassword = Math.random().toString(36).slice(-8) + 'A1!';

    // Use minimal signup - skip profile creation for anonymous users
    const { data, error } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password: randomPassword,
      options: {
        data: {
          role: 'user', // Set role = 'user' in auth.users metadata
          skip_profile_creation: true // Flag to skip profile creation
        },
      },
    });

    if (error) {
      console.error('Anonymous user signup error:', error);

      if (error.message.includes('unique constraint') || error.message.includes('already registered')) {
        throw new Error('An account with this email already exists. Please use a different email or sign in to your existing account.');
      }

      throw new Error(error.message || 'Failed to create user account');
    }

    if (!data.user) {
      throw new Error('No user data received from signup');
    }

    console.log(`✅ Created anonymous user: ${data.user.id} with email: ${email}`);
    return { userId: data.user.id, success: true };
  } catch (err) {
    console.error('Error creating anonymous user:', err);
    throw err;
  }
}

// Validate password strength
export function validatePassword(password: string): { isValid: boolean; message?: string } {
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }

  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }

  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }

  if (!/[0-9]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }

  return { isValid: true };
}

export async function signUp({ email, password, fullName, country }: SignUpData) {
  try {
    // First check if email exists
    const emailExists = await checkEmailExists(email);
    if (emailExists) {
      throw {
        message: 'This email is already registered. Please sign in instead.',
        status: 409
      };
    }

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      throw {
        message: passwordValidation.message,
        status: 400
      };
    }

    // Attempt signup
    const { data, error } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password,
      options: {
        emailRedirectTo: getConfirmEmailRedirectTo(),
        data: {
          full_name: fullName.trim(),
          country: country,
        },
      },
    });

    if (error) {
      // Log the error for debugging
      await supabase.rpc('log_auth_error', {
        p_event_type: 'signup_error',
        p_email: email,
        p_error: error.message,
        p_metadata: { error_code: error.status }
      });

      // Handle specific error cases
      if (error.message.includes('unique constraint')) {
        throw {
          message: 'This email is already registered. Please sign in instead.',
          status: 409
        };
      }
      throw error;
    }

    if (!data.user) {
      throw new Error('No user data received');
    }

    // Profile creation is now handled automatically by database trigger
    // No need to manually call create_user_profile

    const confirmationRequired = needsEmailConfirmation(data.user, data.session);

    // Welcome only when already confirmed (Confirm email disabled / auto-confirm)
    if (!confirmationRequired) {
      try {
        await sendWelcomeIfNeeded(data.user);
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
      }
    }

    // Send admin notification for new user registration
    try {
      await sendEmail({
        templateKey: 'admin_new_user',
        recipientEmail: 'hello@solra.app',
        variables: {
          name: fullName,
          email: email,
          country: country,
          registeredAt: new Date().toLocaleString()
        }
      });
    } catch (adminEmailError) {
      console.error('Failed to send admin notification:', adminEmailError);
      // Continue even if admin email fails
    }

    return {
      user: data.user,
      session: data.session,
      needsEmailConfirmation: confirmationRequired,
    };
  } catch (err) {
    const error = err as AuthError;
    console.error('Signup error:', error);
    throw error;
  }
}

export async function signIn({ email, password }: SignInData) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password,
    });

    if (error) {
      // Log the error for debugging
      await supabase.rpc('log_auth_error', {
        p_event_type: 'signin_error',
        p_email: email,
        p_error: error.message,
        p_metadata: { error_code: error.status }
      });

      if (error.message === 'Invalid login credentials') {
        throw {
          message: 'Invalid email or password. Please check your credentials.',
          status: 401
        };
      }

      // Supabase when Confirm email is ON and user has not confirmed
      if (
        error.message.toLowerCase().includes('email not confirmed') ||
        error.message.toLowerCase().includes('confirm your email')
      ) {
        throw {
          message: 'Please confirm your email before signing in. Check your inbox for the link.',
          status: 403,
          code: 'email_not_confirmed',
        } as AuthError & { code: string };
      }

      throw error;
    }

    if (!data.user) {
      throw new Error('No user data received');
    }

    if (!isEmailConfirmed(data.user)) {
      await signOut();
      throw {
        message: 'Please confirm your email before signing in. Check your inbox for the link.',
        status: 403,
        code: 'email_not_confirmed',
      } as AuthError & { code: string };
    }

    await sendWelcomeIfNeeded(data.user);

    return { user: data.user, session: data.session };
  } catch (err) {
    const error = err as AuthError;
    console.error('Login error:', error);
    throw error;
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function requestPasswordReset(email: string) {
  const isDeployPreview =
    window.location.hostname.startsWith('deploy-preview-') &&
    window.location.hostname.endsWith('--solra.netlify.app');
  const passwordResetOrigin = isDeployPreview
    ? 'https://staging--solra.netlify.app'
    : window.location.origin;
  const redirectTo = `${passwordResetOrigin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
    redirectTo,
  });

  if (error) {
    console.error('Password reset request error:', error);
    throw {
      message: error.message || 'Failed to send password reset email. Please try again.',
      status: error.status,
    } as AuthError;
  }

  return { success: true as const };
}

export async function updatePassword(newPassword: string) {
  const passwordValidation = validatePassword(newPassword);
  if (!passwordValidation.isValid) {
    throw {
      message: passwordValidation.message,
      status: 400,
    } as AuthError;
  }

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) {
    console.error('Update password getUser error:', userError);
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    console.error('Update password error:', error);
    throw {
      message: error.message || 'Failed to update password. Please try again.',
      status: error.status,
    } as AuthError;
  }

  const user = userData?.user;
  if (user?.email) {
    try {
      const name =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        'there';
      const loginLink = `${window.location.origin}/login`;
      await sendPasswordChangeSuccessfulEmail(
        user.email,
        name,
        loginLink
      );
    } catch (emailError) {
      console.error('Failed to send password-change confirmation email:', emailError);
    }
  }

  return { success: true as const };
}

export async function getCurrentUser() {
  try {
    // First check if we have a session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error('Session error:', sessionError);
      return null;
    }
    
    // If no session, return null instead of throwing an error
    if (!session) {
      return null;
    }

    // Get user data if we have a session
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error('User error:', userError);
      return null;
    }
    
    return user;
  } catch (err) {
    console.error('Error getting current user:', err);
    return null;
  }
}

export async function deleteCurrentUser() {
  try {
    // Get the current user's data before deletion
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found');
    }

    // Delete the user
    const { error } = await supabase.auth.admin.deleteUser(user.id);
    if (error) throw error;

    // Send account deletion confirmation email
    // try {
    //   await sendDeleteAccountEmail(user.email, user.user_metadata.full_name);
    // } catch (emailError) {
    //   console.error('Failed to send account deletion email:', emailError);
    //   // Continue with deletion even if email fails
    // }

    // Sign out after successful deletion
    await signOut();

    return { success: true };
  } catch (err) {
    console.error('Error deleting user:', err);
    throw err instanceof Error ? err : new Error('Failed to delete account');
  }
}